/* Build & price (Trailer Town).
   Base prices = the product's Shopify variants. Brands with their own paint chart (RawMaxx) send D.palette [[name, hex, premium]] + D.premiumMsrp. Option list prices + model data = retail-only asset exported from the Stryker Build Desk
   (assets/build-price-<sku prefix>.json). Option rules are derived from the Horizon price book's own wording, see deriveRules().
   window.BuildPrice.Configurator drives one model's length/color/options picker; the single-product page (#build-and-price) uses one,
   the package page (build-package.js) uses one per trailer hitch plus one for the bins. */
(() => {
  const esc = s => String(s ?? "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const usd = n => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  const usd2 = n => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

  const ALL_COLORS = [["Military Green", "#4b5320"], ["Desert Tan", "#c8ad7f"], ["Flame Orange", "#e2571e"], ["Midnight Black", "#15161a"],
    ["Spectra Yellow", "#f3c300"], ["Blue Horizon", "#1f5fae"], ["Lime Green", "#7cc242"], ["Ash Grey", "#8c9094"],
    ["True Red", "#c1121f"], ["Pearl White", "#f3f1ea"], ["Cloud Grey", "#b9bdc2"], ["Navy Blue", "#1f2a44"]];
  const MATCH = "Same as trailer";
  const ORDER = ["TONGUE", "CHASSIS", "DECK", "DUMP", "LIFT SYSTEM", "RUNNING GEAR", "ELECTRICAL AND LIGHTING", "FINISH", "ACCESSORIES"];
  const GROUP_NAMES = [
    [/^AXLE UPGRADE/, "Axle upgrades, standard spring suspension"],
    [/SLIPPER SPRING/, "Heavy-duty slipper spring suspension"],
    [/^RIDEWELL/, "Ridewell air ride suspension"],
    [/^LIFT AXLE/, "Air ride suspension with 1 lift axle"],
    [/^WINCH ADD ON/, "Winch, installed in the storage box on the neck"],
  ];
  // Pick-one categories, matched in order against each option (first match wins).
  const CATS = [
    ["Jacks", o => o.section === "TONGUE" && /jack/i.test(o.desc) && !/stabilizer|swivel/i.test(o.desc)],
    ["Neck toolbox", o => o.section === "TONGUE" && /toolbox/i.test(o.desc)],
    ["Coupler", o => /coupler/i.test(o.desc)],
    ["Side height", o => /high sides/i.test(o.desc)],
    ["Lift", o => /^(telescopic cylinder|hoist)\b/i.test(o.desc)],
    ["Hydraulic oil", o => /low temperature hydraulic oil/i.test(o.desc) && !/jack/i.test(o.desc)],
    ["Remote", o => /\bremote\b/i.test(o.desc)],
    ["Neck D-rings", o => /d-rings/i.test(o.desc) && /neck/i.test(o.desc)],
    ["Deck D-rings", o => /d-rings/i.test(o.desc)],
    ["Sliding ratchets", o => /sliding ratchets/i.test(o.desc)],
    ["Weld-on ratchets", o => /weld on .*ratchets/i.test(o.desc)],
    ["Undercarriage toolboxes", o => /undercarriage toolbox/i.test(o.desc) && !/painted/i.test(o.desc)],
    ["Winch", o => /winch\b/i.test(o.desc) && !/strap|plate|ready/i.test(o.desc)],
    ["Dovetail", o => /dove ?tail/i.test(o.desc)],
    ["Blackwood decking", o => /blackwood/i.test(o.desc)],
    ["Undercoat", o => /zinc primer/i.test(o.desc)],
    ["Tarp", o => /\btarp\b/i.test(o.desc)],
    ["Frame upgrade", o => /frame upgrade$/i.test(o.desc)],
    ["Ramps", o => /ramp/i.test(o.desc) && !/painted/i.test(o.desc) && ["DECK", "DUMP"].includes(o.section)],
    ["Spare tire", o => /SPARE/.test(o.group || "")],
    ["Tire upgrade", o => /TIRES? UPGRADE/.test(o.group || "")],
    ["Aluminum wheels", o => /ALUMINUM WHEELS/.test(o.group || "")],
  ];
  const COUNT_WORDS = { SINGLE: 1, TANDEM: 2, TRIDEM: 3 };
  const title = s => s.toLowerCase().replace(/\b([a-z])/g, m => m.toUpperCase()).replace(/\b(\d+(?:\.\d)?)k\b/gi, "$1K").replace(/\bgvwr\b/gi, "GVWR").replace(/\bgn\b/gi, "GN").replace(/\bbp\b/gi, "BP");
  const cleanDesc = d => d.replace(/\*\*\(/g, "(").replace(/\)?\*\*/g, ")").replace(/\s*\(?\s*DOVETAIL OPTIONS? \)?\s*NOT AVAILABLE FOR \d+' DECK\)?/i, "").replace(/\s+\/\s*\(/g, " (").replace(/\s+/g, " ").trim();
  const perFt = o => /LINEAR FT/.test(o.unit || "");

  // ---------- rules from the price book's wording ----------
  function lengthRule(text) {
    const r = {}; let m;
    if ((m = text.match(/(\d+)\s*ft\s*-\s*(\d+)\s*ft/i))) { r.min = +m[1]; r.max = +m[2]; }
    else if ((m = text.match(/(\d+)\s*ft\s*&\s*up/i))) r.min = +m[1];
    else if ((m = text.match(/^(\d+)\s*ft\b/i))) r.set = [+m[1]];
    if ((m = text.match(/(\d+)'\s*-\s*(\d+)'\s*(deck|only)/i))) { r.min = +m[1]; r.max = +m[2]; }
    if ((m = text.match(/telescopic cylinder (\d+)'\s*dump/i))) r.set = [+m[1]];
    if ((m = text.match(/\((\d+)'\s*&\s*(\d+)'\s*w\//i))) r.set = [+m[1], +m[2]];
    if ((m = text.match(/^(\d+)'\s*DUMP/i))) r.set = [+m[1]];
    if ((m = text.match(/\*(\d+)\s*FT\s*&/i))) r.set = [+m[1]];
    if (!r.set && (m = text.match(/((?:\d+'\s*,\s*)*\d+')\s*dump\b/i))) r.set = m[1].match(/\d+/g).map(Number);   // "Electric Tarp Kit 14', 16' Dump"
    return r;
  }
  function axleSpec(label) {
    const clean = label.replace(/\([^)]*GVWR\)/gi, "").replace(/\([^)]*lbs[^)]*\)/gi, "");
    let ratings = [], m;
    if ((m = clean.match(/(\d+(?:\.\d)?)K\s*-\s*(\d+(?:\.\d)?)K/i))) ratings = [+m[1], +m[2]];
    else ratings = [...clean.matchAll(/\b(\d+(?:\.\d)?)K\b/gi)].map(x => +x[1]);
    const w = clean.match(/\b(SINGLE|TANDEM|TRIDEM)\s+AXLE/i);
    return ratings.length ? { ratings, count: w ? COUNT_WORDS[w[1].toUpperCase()] : null } : null;
  }
  function packageAxle(o) {
    let m;
    if ((m = o.desc.match(/^(\d)\s*-\s*(\d+(?:\.\d)?)k\b.*axle/i))) return { count: +m[1], rating: +m[2] };
    if ((m = o.desc.match(/^standard (\d+(?:\.\d)?)k dual axles/i))) return { count: 2, rating: +m[1] };
    return null;
  }
  function deriveRules(D) {
    const noDovetailAt = [];
    D.options.forEach(o => { const m = o.desc.match(/NOT AVAILABLE FOR (\d+)' DECK/i); if (m) noDovetailAt.push(+m[1]); });
    D.options.forEach(o => {
      const g = o.group || "";
      o.pkg = packageAxle(o) || (/(AXLE|SUSPENSION) UPGRADE/.test(g) && /axle/i.test(o.desc) ? { count: null, rating: null } : null);
      o.len = Object.assign({}, lengthRule(g), lengthRule(o.desc));
      if (/dove ?tail/i.test(o.desc) && noDovetailAt.length) o.len.excl = noDovetailAt.slice();
      o.axle = o.pkg ? null : axleSpec(g);
      o.one = o.pkg ? "Axles & suspension" : (CATS.find(([, t]) => t(o)) || [null])[0];
      o.needs = [];
      if (o.one === "Neck D-rings") o.needs.push([/deck on the neck/i, "Add Deck on the Neck first"]);
      if (o.one === "Sliding ratchets") o.needs.push([/slide track/i, "Add a ratchet slide track first"]);
      if (/toolboxes painted black/i.test(o.desc)) o.needs.push([/undercarriage toolbox/i, "Add an undercarriage toolbox first", true]);
      if (/racks painted black/i.test(o.desc)) o.needs.push([/dunnage rack/i, "Add a dunnage rack first", true]);
      if (/compatible with wood extension/i.test(o.desc)) o.needs.push([/wood extension/i, "Add the wood extension option first"]);
      if (/TELESCOPIC CYLINDER ONLY/i.test(g)) o.needs.push([/^telescopic cylinder/i, "Add the telescopic cylinder first"]);
      o.noTelescopic = /HOIST Lift ONLY/i.test(o.desc);
    });
  }

  // Price file + the product's Shopify variants -> { D, variants }. The base price is the Shopify variant's price when the SKU matches.
  function load(url, shop) {
    return fetch(url).then(r => { if (!r.ok) throw new Error(r.status); return r.json(); }).then(D => {
      D.byId = new Map(D.options.map((o, i) => { o.key = `${o.id}~${i}`; return [o.key, o]; }));
      const src = D.variants || (D.lengths || []).map(l => ({ sku: "", length: l.length, price: l.price, col: -1 }));
      const variants = src.map(v => { const s = shop.find(x => x.sku && x.sku === v.sku) || (!v.sku && shop.find(x => x.length === v.length)); return Object.assign({}, v, s ? { price: s.price, id: s.id } : {}); })
        .filter(v => v.price > 0).sort((a, b) => a.length - b.length || a.price - b.price);
      deriveRules(D);
      return { D, variants };
    });
  }

  // One model's picker. el holds [data-bp] slots: lengths, colors, colorName, options (+ expandAll / clearAll buttons).
  // cfg: lengths(L) -> bool to limit lengths, matchColor() -> the trailer's color (adds a "Same as trailer" swatch), onChange().
  function Configurator(el, D, variants, cfg = {}) {
    const q = n => el.querySelector(`[data-bp="${n}"]`);
    const S = { v: variants[0], color: cfg.matchColor ? MATCH : null, sel: new Map(), open: new Set() };
    const has = key => S.sel.has(key);
    const picked = () => D.options.filter(o => has(o.key));
    const hasWinch = D.options.some(o => o.one === "Winch"), hasLift = D.options.some(o => o.section === "LIFT SYSTEM" && o.one === "Lift");
    const names = { TONGUE: D.pull === "GN" ? "Gooseneck, jacks & toolbox" : "Tongue, jacks & toolbox", CHASSIS: "Frame & undercarriage", DECK: "Deck, tie-downs & ramps", DUMP: "Dump body",
      "LIFT SYSTEM": [hasLift ? "Lift" : "", "remotes", hasWinch ? "winch" : ""].filter(Boolean).join(", ").replace(/^r/, "R").replace(/, (\w+)$/, " & $1"),
      "RUNNING GEAR": "Axles, suspension & tires", "ELECTRICAL AND LIGHTING": "Electrical & lighting", FINISH: "Paint & finish", ACCESSORIES: "Accessories" };
    const present = [...new Set(D.options.map(o => o.section))];
    const SECTIONS = ORDER.filter(k => present.includes(k)).concat(present.filter(k => !ORDER.includes(k))).map(k => [k, names[k] || title(k)]);

    const findPicked = (re, self) => picked().filter(p => p !== self && re.test(p.desc) && !/painted/i.test(p.desc));
    function currentAxle() {
      const pk = picked().find(o => o.pkg && o.pkg.rating);
      if (pk) return pk.pkg;
      return S.v && S.v.axleRating ? { count: S.v.axleCount, rating: S.v.axleRating } : null;
    }
    // Why an option can't be picked right now ("" = available). Options that don't fit the model at all are hidden (see visible()).
    function blocked(o) {
      const L = S.v.length, r = o.len;
      if (r.min && L < r.min) return `Available on ${r.min}′ and longer${r.max ? ` (up to ${r.max}′)` : ""}`;
      if (r.max && L > r.max) return `Available up to ${r.max}′`;
      if (r.set && !r.set.includes(L)) return `Available on ${r.set.map(n => n + "′").join(" and ")} only`;
      if (r.excl && r.excl.includes(L)) return `Not available on ${L}′`;
      if (o.axle) {
        const a = currentAxle();
        if (a && a.rating && !o.axle.ratings.includes(a.rating)) return `For ${o.axle.ratings.map(x => x + "K").join("–")} axles`;
        if (a && a.count && o.axle.count && a.count !== o.axle.count) return `For ${Object.keys(COUNT_WORDS).find(k => COUNT_WORDS[k] === o.axle.count).toLowerCase()} axle setups`;
      }
      for (const [re, msg] of o.needs) if (!findPicked(re, o).length) return msg;
      if (o.noTelescopic && findPicked(/^telescopic cylinder/i, o).length) return "Not available with the telescopic cylinder";
      return "";
    }
    // Hide options that can never apply to this length/axle model (e.g. another length's telescopic cylinder); keep ones that another pick could unlock.
    const visible = o => (o.col === -1 || o.col === S.v.col) && !(o.len.set && !o.len.set.includes(S.v.length) && /telescopic cylinder \d+'|tarp kit [\d', ]+dump|zinc primer|^\d+' dump|^\d+ ft roll off (box|bed)/i.test(o.desc + " " + (o.group || "")));
    function qtyOf(o) {
      if (perFt(o)) return S.v.length;
      if (/toolboxes painted black/i.test(o.desc)) { const tb = findPicked(/undercarriage toolbox/i, o)[0]; const m = tb && tb.desc.match(/(\d+)\s*-\s*side mount/i); return m ? +m[1] : (tb ? 1 : 0); }
      if (/racks painted black/i.test(o.desc)) return findPicked(/dunnage rack/i, o).length;
      if (o.unit === "EACH") return S.sel.get(o.key)?.qty || 1;
      return 1;
    }
    const lineTotal = o => Math.round(o.price * qtyOf(o));
    function settle() {
      for (let i = 0; i < 4; i++) {
        let changed = false;
        for (const k of [...S.sel.keys()]) { const o = D.byId.get(k); if (!o || !visible(o) || blocked(o)) { S.sel.delete(k); changed = true; } }
        if (!changed) break;
      }
    }
    const changed = silent => { if (!silent && cfg.onChange) cfg.onChange(); };
    function toggle(key) {
      const o = D.byId.get(key); if (!o || blocked(o)) return;
      if (has(key)) S.sel.delete(key);
      else { if (o.one) D.options.forEach(x => { if (x.one === o.one) S.sel.delete(x.key); }); S.sel.set(key, { qty: 1 }); }
      settle(); render();
    }

    // ---------- render ----------
    function render(silent) { renderModels(); renderColors(); renderOptions(); changed(silent); }
    function renderModels() {
      const lens = [...new Set(variants.map(v => v.length))].filter(L => !cfg.lengths || cfg.lengths(L)).sort((a, b) => a - b);
      const multi = variants.some(v => variants.filter(x => x.length === v.length).length > 1);
      let html = `<div class="bp-lengths">${lens.map(L => {
        const at = variants.filter(v => v.length === L), from = Math.min(...at.map(v => v.price));
        return `<button type="button" class="bp-len" data-len="${L}" aria-pressed="${S.v.length === L}"><b>${L}′</b><small>${multi && at.length > 1 ? "from " : ""}${usd(from)}${cfg.each ? " each" : ""}</small></button>`;
      }).join("")}</div>`;
      if (multi) {
        const at = variants.filter(v => v.length === S.v.length);
        html += `<p class="bp-sublabel">Axle &amp; GVWR for ${S.v.length}′</p><div class="bp-lengths bp-axles">${at.map(v => `<button type="button" class="bp-len" data-sku="${esc(v.sku)}" aria-pressed="${S.v === v}"><b>${esc(v.axleLabel || v.gvwr || "Standard")}</b><small>${usd(v.price)}</small></button>`).join("")}</div>`;
      }
      q("lengths").innerHTML = html;
    }
    const palette = () => D.palette ? D.palette : (D.colors ? ALL_COLORS.filter(([n]) => D.colors.includes(n)) : ALL_COLORS);
    // Premium paint upcharge (RawMaxx): the picked color's list price, 0 for standard colors.
    const colorCharge = () => { const c = D.palette && D.premiumMsrp && D.palette.find(([n]) => n === S.color); return c && c[2] ? D.premiumMsrp : 0; };
    function colorText() {
      if (S.color === MATCH) { const t = cfg.matchColor(); return t ? `${MATCH} (${t})` : MATCH; }
      return S.color + (colorCharge() ? ` (premium color +${usd(colorCharge())})` : "");
    }
    function renderColors() {
      let list = palette();
      if (cfg.matchColor) { const t = ALL_COLORS.find(([n]) => n === cfg.matchColor()); list = [[MATCH, t ? t[1] : "conic-gradient(#c1121f, #f3c300, #7cc242, #1f5fae, #c1121f)"]].concat(list); }
      if (list.length === 1 && !S.color) S.color = list[0][0];
      q("colors").innerHTML = list.map(([n, hex, prem]) => `<button type="button" class="bp-sw" data-color="${esc(n)}" aria-pressed="${S.color === n}"><i style="background:${hex}"></i>${esc(n)}${prem && D.premiumMsrp ? `<small>+${usd(D.premiumMsrp)}</small>` : ""}</button>`).join("");
      const note = q("colorNote"); if (note && D.palette && D.premiumMsrp) note.textContent = `Standard powder coat colors are included; premium colors add ${usd(D.premiumMsrp)}. Swatches are approximate; ask to see a sample.`;
      q("colorName").textContent = S.color ? colorText() : (D.colors ? `This model comes in ${list.length} colors` : "No color picked yet");
    }
    function groupLabel(g) { const m = GROUP_NAMES.find(([re]) => re.test(g)); return m ? m[1] : title(g.replace(/:\s*$/, "").replace(/\s*OPTIONS?:?\s*$/i, "").trim()); }
    function renderOptions() {
      const by = new Map();
      D.options.filter(visible).forEach(o => { if (!by.has(o.section)) by.set(o.section, []); by.get(o.section).push(o); });
      q("options").innerHTML = SECTIONS.filter(([k]) => by.get(k)?.length).map(([k, label]) => {
        const items = by.get(k), on = items.filter(o => has(o.key)), sub = on.reduce((a, o) => a + lineTotal(o), 0);
        let last = null;
        const rows = items.map(o => {
          let head = "";
          if (o.group !== last) { last = o.group; if (o.group) head = `<div class="bp-grp">${esc(groupLabel(o.group))}</div>`; }
          return head + optRow(o);
        }).join("");
        return `<details class="bp-sec" data-sec="${esc(k)}" ${S.open.has(k) ? "open" : ""}><summary><b>${esc(label)}</b><span class="bp-meta${on.length ? " on" : ""}">${on.length ? `${on.length} added · +${usd(sub)}` : `${items.length} option${items.length === 1 ? "" : "s"}`}</span></summary><div class="bp-sec-body">${rows}</div></details>`;
      }).join("") || `<p class="bp-note">This model has no add-on options in the current price book.</p>`;
    }
    function optRow(o) {
      const on = has(o.key), why = blocked(o), n = qtyOf(o), std = o.unit === "STANDARD";
      let price = std ? "Standard" : usd(o.price), sub = "";
      if (perFt(o)) { price = on ? usd(lineTotal(o)) : usd2(o.price) + "/ft"; sub = on ? `${usd2(o.price)}/ft × ${n}′` : `× your ${S.v.length}′ ${cfg.noun || "deck"} = ${usd(Math.round(o.price * S.v.length))}`; }
      else if (/painted black/i.test(o.desc) && /toolboxes|racks/i.test(o.desc)) { price = on ? usd(lineTotal(o)) : usd(o.price); sub = (/toolbox/i.test(o.desc) ? "per toolbox" : "per rack") + (on ? ` × ${n}` : ""); }
      else if (o.unit === "EACH") { price = on ? usd(lineTotal(o)) : usd(o.price); sub = "each"; }
      else if (o.unit && !std && !/^PER PAIR$/.test(o.unit)) sub = o.unit.toLowerCase();
      else if (o.unit === "PER PAIR") sub = "per pair";
      if (cfg.each && !std) sub = sub ? `${sub}, per ${cfg.noun}` : `per ${cfg.noun}`;
      const qtyUI = on && o.unit === "EACH" ? `<label class="bp-qty">Quantity <input type="number" min="1" max="24" value="${n}" data-qty="${esc(o.key)}"></label>` : "";
      const dis = why || std;
      return `<label class="bp-opt${on ? " on" : ""}${dis ? " off" : ""}"${why ? ` title="${esc(why)}"` : ""}>
        <input type="${o.one ? "radio" : "checkbox"}" data-id="${esc(o.key)}" ${on ? "checked" : ""} ${dis ? "disabled" : ""}${o.one ? ` name="bp-g-${esc(D.code || "")}-${esc(o.one)}"` : ""}>
        <span class="bp-d">${esc(cleanDesc(o.desc))}${why ? `<small>${esc(why)}</small>` : ""}${qtyUI}</span>
        <span class="bp-p">${std ? price : "+" + price}${sub ? `<small>${esc(sub)}</small>` : ""}</span></label>`;
    }
    function totals() { const p = picked(); const color = colorCharge(); const opt = p.reduce((a, o) => a + lineTotal(o), 0) + color; return { picks: p, opt, color, total: S.v.price + opt }; }
    function modelLabel() { const multi = variants.filter(v => v.length === S.v.length).length > 1; return `${S.v.length}′${multi && S.v.axleLabel ? ` ${S.v.axleLabel}` : ""}`; }
    // "HDZ-512 6' High Sides x2 $1,788" items for the request text
    const optText = () => totals().picks.map(o => `${o.id ? o.id + " " : ""}${cleanDesc(o.desc).slice(0, 60)}${qtyOf(o) > 1 && !perFt(o) ? ` x${qtyOf(o)}` : ""} ${usd(lineTotal(o))}`).concat(colorCharge() ? [`Premium color ${usd(colorCharge())}`] : []).join("; ");

    function pickVariant(v, silent) { if (v) S.v = v; settle(); render(silent); }
    function pickLength(L, silent) { const same = variants.filter(v => v.length === L); if (same.length) pickVariant(same.find(v => v.axleLabel === S.v.axleLabel) || same[0], silent); }

    // ---------- events ----------
    el.addEventListener("click", e => {
      const l = e.target.closest("[data-len]");
      if (l) { pickLength(+l.dataset.len); return; }
      const s = e.target.closest("[data-sku]"); if (s) { pickVariant(variants.find(v => v.sku === s.dataset.sku)); return; }
      const c = e.target.closest("[data-color]"); if (c) { S.color = c.dataset.color; renderColors(); changed(); return; }
      const inp = e.target.closest("input[data-id]"); if (inp) { e.preventDefault(); toggle(inp.dataset.id); return; }
      const b = e.target.closest("[data-bp]"); const name = b && b.dataset.bp;
      if (name === "expandAll") { const all = S.open.size < SECTIONS.length; S.open = new Set(all ? SECTIONS.map(x => x[0]) : []); b.textContent = all ? "Collapse all" : "Expand all"; renderOptions(); return; }
      if (name === "clearAll") { S.sel.clear(); render(); }
    });
    el.addEventListener("toggle", e => { const d = e.target; if (d.matches && d.matches("details.bp-sec")) d.open ? S.open.add(d.dataset.sec) : S.open.delete(d.dataset.sec); }, true);
    el.addEventListener("change", e => {
      const id = e.target.dataset.qty;
      if (id && S.sel.has(id)) { S.sel.get(id).qty = Math.min(24, Math.max(1, Math.round(+e.target.value || 1))); renderOptions(); changed(); }
    });

    return { S, D, variants, render, renderColors, pickVariant, pickLength, totals, modelLabel, optText, colorText, colorCharge, qtyOf, lineTotal, blocked, visible };
  }

  // The Stryker Pro quick-quote form (snippets/trailertown-pdp-quote-embed.liquid, rendered with auto: false) sits open
  // under "Get your final price". The build rides in the form's URL (build_details), so the form is (re)loaded with the
  // current build: first once the builder is ready, then after each change while the visitor hasn't started typing in it.
  // Once they have, a change only offers an "update the form" link, so their details aren't wiped without asking.
  const READY = "Your build is attached. Fill in your details and send.";
  function formSync(root, getText, getDetail) {
    const note = root.querySelector('[data-bp="formNote"]');
    let attached = null, touched = false, timer = null;
    const frame = () => root.querySelector(".stryker-pdp-mount iframe");
    // Focus moving into the cross-origin form shows up as a window blur with the iframe as the active element.
    window.addEventListener("blur", () => setTimeout(() => { const f = frame(); if (f && document.activeElement === f) touched = true; }, 0));
    window.addEventListener("stryker:lead_submitted", () => { touched = true; });
    function load() {
      clearTimeout(timer);
      const embed = root.querySelector(".stryker-pdp-embed");
      if (!embed) return;
      const text = getText(); attached = text; touched = false;
      window.dispatchEvent(new CustomEvent("buildprice:request", { detail: Object.assign({ text }, getDetail()) }));
      embed.setAttribute("data-stryker-build", text);
      const state = embed.getAttribute("data-stryker-pdp-state");
      if (state === "mounted" || state === "loading") {   // reload the form with the new build
        clearTimeout(embed.__strykerPdpTimer); embed.__strykerPdpTimer = null;
        embed.querySelectorAll(".stryker-pdp-mount iframe").forEach(f => f.remove());
        embed.setAttribute("data-stryker-pdp-state", "idle");
      }
      note.textContent = READY;
      embed.querySelector("[data-stryker-pdp-trigger]")?.click();
    }
    function update() {
      const text = getText();
      if (text === attached) { note.textContent = READY; clearTimeout(timer); return; }
      if (attached === null) { load(); return; }
      if (!touched) { clearTimeout(timer); timer = setTimeout(load, 900); return; }
      note.innerHTML = `You've changed your build since you started the form. <button type="button" class="bp-link" data-bp-refresh>Update the form with your changes</button> (you'll re-enter your details).`;
    }
    root.addEventListener("click", e => { if (e.target.closest("[data-bp-refresh]")) load(); });
    return { update, load };
  }
  const readJSON = el => { try { return JSON.parse(el.textContent); } catch (_) { return []; } };

  window.BuildPrice = { load, Configurator, formSync, readJSON, esc, usd, cleanDesc, perFt };

  // ---------- single-product page ----------
  const root = document.getElementById("build-and-price");
  if (!root) return;
  const q = n => root.querySelector(`[data-bp="${n}"]`);
  const bar = document.querySelector('[data-bp="bar"]');
  let C = null;
  const form = formSync(root, () => buildText(), () => ({ total: C.totals().total, sku: C.S.v.sku }));

  function renderSummary() {
    const t = C.totals(), S = C.S;
    q("sumTitle").textContent = `${C.modelLabel()} ${root.dataset.product}`;
    q("sumColor").textContent = S.color ? `Color: ${S.color}` : "Color: not picked yet";
    q("lines").innerHTML = `<div class="bp-line base"><span>${esc(C.modelLabel())} trailer, standard features</span><span>${usd(S.v.price)}</span></div>` +
      (t.color ? `<div class="bp-line"><span>Premium color: ${esc(S.color)}</span><span>+${usd(t.color)}</span></div>` : "") +
      t.picks.map(o => `<div class="bp-line"><span>${esc(cleanDesc(o.desc))}${C.qtyOf(o) > 1 && !perFt(o) ? ` × ${C.qtyOf(o)}` : ""}</span><span>+${usd(C.lineTotal(o))}</span></div>`).join("");
    q("total").textContent = usd(t.total);
    if (bar) { bar.querySelector('[data-bp="barTotal"]').textContent = usd(t.total); bar.querySelector('[data-bp="barSub"]').textContent = `${C.modelLabel()} · ${t.picks.length} option${t.picks.length === 1 ? "" : "s"}`; }
    form.update();
  }
  function buildText() {
    const t = C.totals(), S = C.S;
    return `${root.dataset.product} | ${C.modelLabel()} (${S.v.sku}) ${usd(S.v.price)} | Color: ${S.color || "not picked"} | Options: ${C.optText() || "none"} | List price as built: ${usd(t.total)}`;
  }

  load(root.dataset.prices, readJSON(root.querySelector("[data-bp-variants]"))).then(({ D, variants }) => {
    if (!variants.length) throw new Error("no variants");
    C = Configurator(root, D, variants, { onChange: renderSummary });
    const pick = new URLSearchParams(location.search).get("variant");
    C.pickVariant(variants.find(v => String(v.id) === pick) || variants[0]);
    window.__buildPrice = C;   // for QA
  }).catch(() => {
    q("options").innerHTML = `<p class="bp-note">Options didn't load. Refresh the page, or call ${esc(root.dataset.phone)} for a quote.</p>`;
    q("formNote").textContent = "Fill in your details and we'll get back to you with pricing.";
    root.querySelector("[data-stryker-pdp-trigger]")?.click();   // still offer the form, just without a build
  });
})();
